/**
 * Order state machine — the single source of truth for worker, site and admin.
 *
 * created                 payment open, nothing bought yet
 * provisioning            money is ours (claimed); eSIM Go purchase in flight
 * on_hold                 manual approval gate held delivery
 * paid_pending_provision  money is ours but provisioning failed retryably
 * provisioned             eSIM delivered
 * failed                  payment window closed with no money
 * refunded                operator recorded an out-of-band crypto refund
 *
 * `on_hold` and `paid_pending_provision` are INTERNAL. Customers must never
 * see them: both mask to "provisioning" via customerStatus().
 */
export const ORDER_STATUSES = [
    "created",
    "provisioning",
    "on_hold",
    "paid_pending_provision",
    "provisioned",
    "failed",
    "refunded",
];
/**
 * The one status each operator action on an order starts from. The admin
 * offers an action only there. The worker fences approve, retry and the refund
 * on it directly; close goes through the customer's Cancel
 * (`worker/src/domain/crypto/cancel.ts`), which checks the same status. A test
 * over every status and action (`worker/test/admin-orders.test.ts`) holds the
 * worker to this table.
 */
export const ORDER_ACTION_FROM = {
    approve: "on_hold",
    retry: "paid_pending_provision",
    close: "created",
    refund: "provisioned",
};
/** Statuses the storefront is allowed to render. */
export const CUSTOMER_ORDER_STATUSES = [
    "created",
    "provisioning",
    "provisioned",
    "failed",
    "refunded",
];
const INTERNAL_TO_CUSTOMER = {
    on_hold: "provisioning",
    paid_pending_provision: "provisioning",
};
/**
 * Allow-list mask: a status this build has never heard of collapses to
 * "provisioning" rather than leaking a raw internal name.
 */
export function customerStatus(status) {
    if (CUSTOMER_ORDER_STATUSES.includes(status)) {
        return status;
    }
    // Object.hasOwn, not a plain lookup: an object literal answers "constructor"
    // and "__proto__" with inherited values, so a `??` fallback never fires for
    // them and this leak-prevention function would return a function.
    if (Object.hasOwn(INTERNAL_TO_CUSTOMER, status)) {
        return INTERNAL_TO_CUSTOMER[status];
    }
    return "provisioning";
}
export const ORDER_KINDS = ["purchase", "topup"];
/**
 * The order allowance: this many orders from one network in an hour, then a
 * block this many hours long. Here so the admin's words cannot drift from the
 * worker's rule; why these numbers is in `worker/src/domain/ip-block.ts`.
 */
/**
 * What a refill is told for every number the shop will not refill -- never
 * ours, closed, past its floor. One sentence, because a second would say which
 * is which. Shared so the storefront's test double answers in the worker's
 * own words rather than a copy of them.
 */
export const NO_SUCH_LINE_MESSAGE = "We could not find an active eSIM with that number. If your line has expired, order a new eSIM.";
export const IP_ORDERS_PER_HOUR = 20;
export const IP_BLOCK_HOURS = 2;
//# sourceMappingURL=order.js.map